// frontend/src/pages/ManageProfile.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { updateProfile, changePassword } from '../services/authService';
import api from '../services/api';
import Swal from 'sweetalert2';

const ManageProfile = ({ user, setUser }) => {
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [school, setSchool] = useState(null);

  // Load user data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await api.get('/auth/profile');
        const userData = response.data;
        
        setProfileData({
          name: userData.name || '',
          email: userData.email || '',
          phone: userData.phone || '',
          address: userData.address || ''
        });
        
        // If school admin, fetch school info
        if (userData.role === 'admin' && userData.school_id) {
          const schoolRes = await api.get(`/schools/${userData.school_id}`);
          setSchool(schoolRes.data);
        }
        
      } catch (error) {
        console.error('Error fetching user data:', error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถโหลดข้อมูลผู้ใช้ได้ กรุณาลองใหม่อีกครั้ง',
          confirmButtonText: 'ตกลง'
        });
      }
    };

    if (user) {
      fetchUserData();
    }
  }, [user]);

  // Handle profile form change
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle password form change
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  // Update profile
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await updateProfile(profileData);
      
      // Update user in state
      setUser((prev) => ({ ...prev, name: profileData.name }));
      
      Swal.fire({
        icon: 'success',
        title: 'สำเร็จ',
        text: 'อัปเดตข้อมูลส่วนตัวเรียบร้อยแล้ว',
        confirmButtonText: 'ตกลง'
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: error.message || 'ไม่สามารถอัปเดตข้อมูลส่วนตัวได้ กรุณาลองใหม่อีกครั้ง',
        confirmButtonText: 'ตกลง'
      });
    } finally {
      setLoading(false);
    }
  };

  // Change password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      Swal.fire({
        icon: 'error',
        title: 'รหัสผ่านไม่ตรงกัน',
        text: 'รหัสผ่านใหม่และยืนยันรหัสผ่านไม่ตรงกัน กรุณาตรวจสอบอีกครั้ง',
        confirmButtonText: 'ตกลง'
      });
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      Swal.fire({
        icon: 'error',
        title: 'รหัสผ่านไม่ปลอดภัย',
        text: 'รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร',
        confirmButtonText: 'ตกลง'
      });
      return;
    }
    
    setLoading(true);

    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      
      // Clear password fields
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      Swal.fire({
        icon: 'success',
        title: 'สำเร็จ',
        text: 'เปลี่ยนรหัสผ่านเรียบร้อยแล้ว',
        confirmButtonText: 'ตกลง'
      });
    } catch (error) {
      console.error('Error changing password:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: error.message || 'ไม่สามารถเปลี่ยนรหัสผ่านได้ กรุณาลองใหม่อีกครั้ง',
        confirmButtonText: 'ตกลง'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="wrapper">
      <Header user={user} title="จัดการข้อมูลส่วนตัว" />
      <Sidebar user={user} />

      <div className="content-wrapper">
        <section className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1>จัดการข้อมูลส่วนตัว</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  {user.role === 'super_admin' && (
                    <li className="breadcrumb-item">
                      <Link to="/super-admin/dashboard">หน้าหลัก</Link>
                    </li>
                  )}
                  {user.role === 'admin' && (
                    <li className="breadcrumb-item">
                      <Link to="/admin/dashboard">หน้าหลัก</Link>
                    </li>
                  )}
                  {user.role === 'referee' && (
                    <li className="breadcrumb-item">
                      <Link to="/referee/dashboard">หน้าหลัก</Link>
                    </li>
                  )}
                  <li className="breadcrumb-item active">จัดการข้อมูลส่วนตัว</li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-md-4">
                <div className="card card-primary card-outline">
                  <div className="card-body box-profile">
                    <div className="text-center">
                    <img
                        className="profile-user-img img-fluid img-circle"
                        src="https://www.w3schools.com/HOWTO/img_avatar.png"
                        alt="User profile"
                        style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                        />
                    </div>
                    

                    <h3 className="profile-username text-center">{user.name}</h3>

                    <p className="text-muted text-center">
                      {user.role === 'super_admin'
                        ? 'ผู้ดูแลระบบเขตพื้นที่'
                        : user.role === 'admin'
                        ? 'ผู้ดูแลโรงเรียน'
                        : 'กรรมการประเมิน'}
                    </p>

                    <ul className="list-group list-group-unbordered mb-3">
                      <li className="list-group-item">
                        <b>ชื่อผู้ใช้</b> <span className="float-right">{user.username}</span>
                      </li>
                      {user.role === 'admin' && school && (
                        <>
                          <li className="list-group-item">
                            <b>โรงเรียน</b> <span className="float-right">{school.name}</span>
                          </li>
                          <li className="list-group-item">
                            <b>รหัสโรงเรียน</b> <span className="float-right">{school.code}</span>
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>

                <div className="card card-primary">
                  <div className="card-header">
                    <h3 className="card-title">ข้อมูลบัญชีผู้ใช้</h3>
                  </div>
                  <div className="card-body">
                    <strong><i className="fas fa-user mr-1"></i> ชื่อผู้ใช้งาน</strong>
                    <p className="text-muted">{user.username}</p>
                    <hr />

                    <strong><i className="fas fa-users-cog mr-1"></i> สิทธิ์การใช้งาน</strong>
                    <p className="text-muted">
                      {user.role === 'super_admin'
                        ? 'ผู้ดูแลระบบเขตพื้นที่'
                        : user.role === 'admin'
                        ? 'ผู้ดูแลโรงเรียน'
                        : 'กรรมการประเมิน'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-md-8">
                <div className="card">
                  <div className="card-header p-2">
                    <ul className="nav nav-pills">
                      <li className="nav-item">
                        <a className="nav-link active" href="#profile" data-bs-toggle="tab">
                          ข้อมูลส่วนตัว
                        </a>
                      </li>
                      <li className="nav-item">
                        <a className="nav-link" href="#password" data-bs-toggle="tab">
                          เปลี่ยนรหัสผ่าน
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div className="card-body">
                    <div className="tab-content">
                      <div className="active tab-pane" id="profile">
                        <form onSubmit={handleUpdateProfile}>
                          <div className="form-group">
                            <label htmlFor="name">ชื่อ-นามสกุล</label>
                            <input
                              type="text"
                              className="form-control"
                              id="name"
                              name="name"
                              value={profileData.name}
                              onChange={handleProfileChange}
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="email">อีเมล</label>
                            <input
                              type="email"
                              className="form-control"
                              id="email"
                              name="email"
                              value={profileData.email}
                              onChange={handleProfileChange}
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="phone">เบอร์โทรศัพท์</label>
                            <input
                              type="text"
                              className="form-control"
                              id="phone"
                              name="phone"
                              value={profileData.phone}
                              onChange={handleProfileChange}
                              maxLength="10"
                              pattern="[0-9]{10}"
                              title="เบอร์โทรศัพท์ต้องเป็นตัวเลข 10 หลัก"
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="address">ที่อยู่</label>
                            <textarea
                              className="form-control"
                              id="address"
                              name="address"
                              value={profileData.address}
                              onChange={handleProfileChange}
                              rows="3"
                            ></textarea>
                          </div>
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                กำลังบันทึก...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-2"></i>
                                บันทึกข้อมูล
                              </>
                            )}
                          </button>
                        </form>
                      </div>

                      <div className="tab-pane" id="password">
                        <form onSubmit={handleChangePassword}>
                          <div className="form-group">
                            <label htmlFor="currentPassword">รหัสผ่านปัจจุบัน</label>
                            <input
                              type="password"
                              className="form-control"
                              id="currentPassword"
                              name="currentPassword"
                              value={passwordData.currentPassword}
                              onChange={handlePasswordChange}
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="newPassword">รหัสผ่านใหม่</label>
                            <input
                              type="password"
                              className="form-control"
                              id="newPassword"
                              name="newPassword"
                              value={passwordData.newPassword}
                              onChange={handlePasswordChange}
                              required
                              minLength="6"
                            />
                            <small className="form-text text-muted">
                              รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร
                            </small>
                          </div>
                          <div className="form-group">
                            <label htmlFor="confirmPassword">ยืนยันรหัสผ่านใหม่</label>
                            <input
                              type="password"
                              className="form-control"
                              id="confirmPassword"
                              name="confirmPassword"
                              value={passwordData.confirmPassword}
                              onChange={handlePasswordChange}
                              required
                              minLength="6"
                            />
                          </div>
                          <button
                            type="submit"
                            className="btn btn-danger"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                กำลังเปลี่ยนรหัสผ่าน...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-key me-2"></i>
                                เปลี่ยนรหัสผ่าน
                              </>
                            )}
                          </button>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default ManageProfile;